import { User, UserRole } from '../types';

/**
 * Check if user is a super admin
 */
export const isSuperAdmin = (user: User | null): boolean => {
	return user?.role === UserRole.SUPER_ADMIN;
};

/**
 * Check if user is a platform admin
 */
export const isPlatformAdmin = (user: User | null): boolean => {
	return user?.role === UserRole.PLATFORM_ADMIN;
};

/**
 * Check if user is a client user
 */
export const isClientUser = (user: User | null): boolean => {
	return user?.role === UserRole.CLIENT_USER;
};

/**
 * Check if user has admin privileges (SUPER_ADMIN or PLATFORM_ADMIN)
 */
export const isAdmin = (user: User | null): boolean => {
	return user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.PLATFORM_ADMIN;
};

/**
 * Check if user can create clients
 */
export const canCreateClient = (user: User | null): boolean => {
	return isAdmin(user);
};

/**
 * Check if user can update clients
 */
export const canUpdateClient = (user: User | null): boolean => {
	return isAdmin(user);
};

/**
 * Check if user can delete/deactivate clients
 */
export const canDeleteClient = (user: User | null): boolean => {
	return isAdmin(user);
};

/**
 * Check if user can view all clients
 */
export const canViewAllClients = (user: User | null): boolean => {
	return isAdmin(user);
};

/**
 * Check if user can perform system maintenance
 */
export const canPerformMaintenance = (user: User | null): boolean => {
	return isSuperAdmin(user);
};

/**
 * Check if user can perform bulk operations
 */
export const canPerformBulkOperations = (user: User | null): boolean => {
	return isSuperAdmin(user);
};

/**
 * Check if user can create users
 */
export const canCreateUser = (user: User | null): boolean => {
	// All roles can create users (with restrictions handled by backend)
	return !!user;
};

/**
 * Check if user can view all sync logs
 */
export const canViewAllSyncLogs = (user: User | null): boolean => {
	return isAdmin(user);
};

/**
 * Get user's display name
 */
export const getUserDisplayName = (user: User | null): string => {
	if (!user) return 'Guest';
	return `${user.firstName} ${user.lastName}`.trim() || user.email;
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: UserRole): string => {
	switch (role) {
		case UserRole.SUPER_ADMIN:
			return 'Super Admin';
		case UserRole.PLATFORM_ADMIN:
			return 'Platform Admin';
		case UserRole.CLIENT_USER:
			return 'Client User';
		default:
			return 'Unknown';
	}
};
